import { Rebalancing } from '../model';
import { AllowanceFactory, TokenFactory, TradeFactory } from 'common';
import { RebalancingResponseDto, SwapResponseDto } from '../dto';

export class RebalancingFactory {
  public static updateFromDto(
    rebalancing: Rebalancing,
    dto: RebalancingResponseDto,
  ): Rebalancing {
    if (dto) {
      rebalancing.id = dto.id;
      if (dto.creationDate) {
        rebalancing.creationDate = new Date(dto.creationDate);
      }
      if (dto.modificationDate) {
        rebalancing.modificationDate = new Date(dto.modificationDate);
      }
      rebalancing.baseValueInUsd = dto.baseValueInUsd;
      rebalancing.sellValueInUsd = dto.sellValueInUsd;
      rebalancing.estimatedValueLossInUsd = dto.estimatedValueLossInUsd;
      rebalancing.estimatedTargetValueInUsd = dto.estimatedTargetValueInUsd;
      rebalancing.estimatedReceiveValueInUsd = dto.estimatedReceiveValueInUsd;
      rebalancing.minReceiveValueInUsd = dto.minReceiveValueInUsd;
      rebalancing.estimatedImpact = dto.estimatedImpact;
      rebalancing.maxImpact = dto.maxImpact;
      if (dto.estimatedGasFees) {
        rebalancing.estimatedGasFees = BigInt(dto.estimatedGasFees);
      }
      rebalancing.estimatedGasFeesInUsd = dto.estimatedGasFeesInUsd;
      rebalancing.estimatedProtocolFeesInUsd = dto.estimatedProtocolFeesInUsd;
      rebalancing.state = dto.state;

      if (dto.expirationTimestamp) {
        rebalancing.expirationTimestamp = new Date(dto.expirationTimestamp);
      }
      if (dto.earliestRefreshQuotesTimestamp) {
        rebalancing.earliestRefreshQuotesTimestamp = new Date(
          dto.earliestRefreshQuotesTimestamp,
        );
      }
      if (dto.suggestedRefreshQuotesTimestamp) {
        rebalancing.suggestedRefreshQuotesTimestamp = new Date(
          dto.suggestedRefreshQuotesTimestamp,
        );
      }

      rebalancing.txHandler = dto.txHandler;
      rebalancing.txData = dto.txData;
      if (dto.txValue) {
        rebalancing.txValue = BigInt(dto.txValue);
      }
    }
    rebalancing.executable = dto.executable;
    if (dto.requiredAllowances) {
      rebalancing.allowances = dto.requiredAllowances?.map((allowance, index) =>
        AllowanceFactory.createFromDto(allowance, index),
      );
    }
    if (dto.trades) {
      rebalancing.trades = dto.trades.map(tradeDto =>
        TradeFactory.createFromDto(
          tradeDto,
          TokenFactory.createTokenFromDto(tradeDto.from),
          TokenFactory.createTokenFromDto(tradeDto.to),
        ),
      );
    }
    return rebalancing;
  }

  /**
   * Experimental: Some values are missing here
   * @param dto
   */
  public static createFromSwapDto(dto: SwapResponseDto): Rebalancing {
    const rebalancing = new Rebalancing();

    if (dto) {
      rebalancing.id = dto.id;
      if (dto.creationDate) {
        rebalancing.creationDate = new Date(dto.creationDate);
      }
      if (dto.modificationDate) {
        rebalancing.modificationDate = new Date(dto.modificationDate);
      }
      rebalancing.baseValueInUsd = dto.sellAmountInUsd;
      rebalancing.sellValueInUsd = dto.sellAmountInUsd;
      rebalancing.estimatedTargetValueInUsd = dto.buyAmountInUsd;
      rebalancing.estimatedReceiveValueInUsd = dto.buyAmountInUsd;
      rebalancing.minReceiveValueInUsd = dto.minBuyAmountInUsd;
      rebalancing.estimatedImpact = dto.priceImpactBps / 10000;
      rebalancing.maxImpact = dto.maxPriceImpactBps / 10000;
      if (dto.transaction.gasLimit) {
        rebalancing.estimatedGasFees = BigInt(dto.transaction.gasLimit);
      }

      if (dto.expiresAt) {
        rebalancing.expirationTimestamp = new Date(dto.expiresAt);
      } else {
        rebalancing.expirationTimestamp = new Date(Date.now() + 2 * 60 * 1000);
      }

      rebalancing.txHandler = dto.transaction.to;
      rebalancing.txData = dto.transaction.data;
      if (dto.transaction.value) {
        rebalancing.txValue = BigInt(dto.transaction.value);
      }
    }
    rebalancing.executable = true;
    rebalancing.trades = [];

    return rebalancing;
  }
}
